import { Component, OnInit, ViewChild, NgZone, ChangeDetectorRef } from '@angular/core';
import { IonContent, NavController, ModalController, MenuController, LoadingController } from '@ionic/angular';
import { ActivatedRoute, Router, NavigationExtras } from '@angular/router';

import { PublicEventInfo, EventBarberAndTimeslots, EventServiceOffering } from '../../models/public-event-info';
import { Constants } from '../../models/constants';

import { EventService } from '../../services/event-service';
import { Alert } from '../../services/alert';
import { UserService } from '../../services/user-service';
import { Util } from '../../services/util';
import { ServicesListPage } from '../services-list/services-list.page';
import { DeviceService } from '../../services/device-service';
import { MetaTagService } from 'src/app/services/meta-tag-service';
import { ObservableService } from 'src/app/services/observable-service';

export interface TimeslotIndex {
  barberIndex: number
  timeslotIndex: number
}

@Component({
  selector: 'app-event-main',
  templateUrl: './event-main.page.html',
  styleUrls: ['./event-main.page.scss'],
})
export class EventMainPage implements OnInit {

  @ViewChild('content', { read: IonContent }) content: IonContent;

  eventInfo: PublicEventInfo
  eventID: string
  selectedIndex?: TimeslotIndex
  prosAndTimeslots?: EventBarberAndTimeslots[]
  selectedService?: EventServiceOffering

  headerSepOpacity = 0
  headerSepOpaquenessStartPx = 0;
  headerSepOpaquenessEndPx = 10;
  slackUrl = ''

  constructor(
    private navCtrl: NavController,
    private modalCtrl: ModalController,
    private eventService: EventService,
    private alert: Alert,
    private loadingCtrl: LoadingController,
    private zone: NgZone,
    private userService: UserService,
    private observables: ObservableService,
    private router: Router,
    private route: ActivatedRoute,
    private changeRef: ChangeDetectorRef,
    public deviceService: DeviceService,
    private menuCtrl: MenuController,
    private metaTagService: MetaTagService
  ) {}

  ngOnInit() {

    this.menuCtrl.enable(true);

    this.eventID = this.route.snapshot.paramMap.get('eventID')

    if (this.router.getCurrentNavigation() && this.router.getCurrentNavigation().extras.state) {
      // We came from nav push. Load in initial props.
      const { state: navState } = this.router.getCurrentNavigation().extras
      if (navState) {
        this.eventInfo = navState.publicEventInfo
        this.updatePageTitle()

        if (!this.eventInfo) {
          this.refreshData()
        }
      }
    } else {
      this.refreshData()
    }

    this.updateSlackUrl()
  }

  ionViewWillEnter() {
  }

  ionViewDidEnter() {
    this.observables.publishSetCurrentMenuRoot('/events')
  }

  refreshData(refresher?: any) {
    this.eventService.pullPublicEventInfo(this.eventID, (eventInfo: PublicEventInfo) => {

      if (refresher) refresher.target.complete()
      this.eventInfo = eventInfo

      this.updatePageTitle()

      this.updateSlackUrl()

    }, (errorMsg: string) => {

      if (refresher) refresher.target.complete()

      if (!Util.isNavigationError(errorMsg)) {
        this.alert.show("Error", errorMsg);
      }

    });
  }

  async fetchTimeslots(refresher?: any) {
    if (refresher) refresher.target.complete()
    if (this.selectedService) {
      const loading = await this.loadingCtrl.create()
      loading.present()

      this.eventService.pullEventProsAndTimeslotsForService(this.eventID, this.selectedService.id, (prosAndTimeslots: EventBarberAndTimeslots[]) => {
        loading.dismiss()
        this.prosAndTimeslots = prosAndTimeslots.filter((proAndTimeslots) => !proAndTimeslots.errorMessage)
        this.changeRef.detectChanges()
      }, (errMsg: string) => {
        console.log(errMsg)
        this.alert.show('Oops', 'Something went wrong fetching the available timeslots. Please select your service again.')
        loading.dismiss()
      })
    }
  }

  goBack() {
    if (this.eventInfo && this.eventInfo.mobileEventCode) {
      this.navCtrl.navigateBack(`/event/${this.eventInfo.mobileEventCode}`)
      return
    }

    this.navCtrl.navigateBack('/events')
  }

  private updatePageTitle() {
      if (!this.eventInfo) return

      let timezone = {offset: this.eventInfo.timezoneOffset}
      let shortDate = Util.shortMonthDay(this.eventInfo.startTime, timezone)
      this.metaTagService.updateTitle(`${this.eventInfo.name}, ${shortDate}`)
      this.changeRef.detectChanges()
  }

  private updateSlackUrl() {
    if (this.eventInfo) {
      const redirectUri = Constants.DEV_MODE ? Constants.DEV_SLACK_REDIRECT_URI : 'https://app.getshortcut.co/setup-slack'
      this.slackUrl = `https://slack.com/oauth/authorize?scope=incoming-webhook&client_id=13329309285.894738830231&redirect_uri=${redirectUri}&state=${this.eventInfo.mobileEventCode}`
    }
  }

  scrollHandler(scrollEvent: any) {

    let scrollValue = scrollEvent.detail.scrollTop;

    var headerSepOpacity = 0

    if (scrollValue < this.headerSepOpaquenessStartPx) {
      headerSepOpacity = 0
    } else if (scrollValue > this.headerSepOpaquenessEndPx) {
      headerSepOpacity = 1
    } else {
      let range = this.headerSepOpaquenessEndPx - this.headerSepOpaquenessStartPx
      let difference = scrollValue - this.headerSepOpaquenessStartPx
      headerSepOpacity = difference / range;
    }

    this.zone.run(() => {
      // since titleOpacity is data-binded,
      // the update needs to happen in zone
      this.headerSepOpacity = headerSepOpacity
    })
  }




  // User actions
  goToBarber(barberLink: string) {
    this.navCtrl.navigateForward(`/pro/${barberLink}?event=${this.eventID}`);
  }

  goToBarberWithTimeslots(proTimeslot: EventBarberAndTimeslots) {
    let navigationExtras = {
      state: {
        selectedService: this.selectedService,
        timeslots: proTimeslot.timeslots,
        eventID: this.eventID,
        eventInfo: this.eventInfo
      }
    }
    this.navCtrl.navigateForward(`/pro/${proTimeslot.pro.link}?event=${this.eventID}`, navigationExtras);
  }

  timeslotPressed(barberIndex: number, timeslotIndex: number) {

    this.selectedIndex = {
      barberIndex: barberIndex,
      timeslotIndex: timeslotIndex
    }

  }

  async continuePressed() {

    if (!this.selectedIndex) {
      return
    }

    let barber = this.prosAndTimeslots[this.selectedIndex.barberIndex].pro
    let timeslot = this.prosAndTimeslots[this.selectedIndex.barberIndex].timeslots[this.selectedIndex.timeslotIndex]

    if (timeslot.locked) {
      this.alert.show('Locked', 'Someone else is currently viewing this timeslot. Please choose a different time or wait a bit and pull to refresh.')
      return
    }

    let loading = await this.loadingCtrl.create()
    loading.present()

    this.eventService.lockEventTimeslot(timeslot.id, async () => {

      loading.dismiss()
      let navigationExtras = {
        state: {
          eventApptInfo: {
            eventInfo: this.eventInfo,
            selectedBarber: barber,
            selectedTimeslot: timeslot,
            eventID: this.eventID,
            selectedService: this.selectedService
          }
        }
      }
      this.navCtrl.navigateForward('/event-confirm-booking', navigationExtras)

    }, (errorMsg: string) => {

      loading.dismiss()
      this.alert.show('Error', errorMsg)

    })

  }

  async selectServicePressed() {
    let servicesModal = await this.modalCtrl.create({
      component: ServicesListPage,
      componentProps: {
        eventTabTitle: this.eventInfo.name,
        eventID: this.eventID,
        eventServiceOfferings: this.eventInfo.serviceOfferings,
        eventCurrency: this.eventInfo.currency
      },
      cssClass: 'SCWEB-services-modal'
    })

    servicesModal.onDidDismiss().then((val) => {
      if (!val || !val.data || !val.data.selectedService) {
        return
      }

      this.selectedService = val.data.selectedService
      this.fetchTimeslots()
    })

    servicesModal.present();
  }

  async joinWaitlist() {

    if (this.userService.user.isGuest) {
      let preventMainRefresh = true
      const source = 'event'
      this.observables.publishGoToSignUp(preventMainRefresh, source)
      return;
    }

    let loading = await this.loadingCtrl.create()
    loading.present()

    this.eventService.joinWaitlist(this.eventID, () => {

      loading.dismiss()
      this.alert.show('Success', "You've been added to the waitlist. You'll be notified with any updates to availability.", 'Ok', () => {
        this.observables.publishJoinedWaitlist()
      })

    }, (errorMsg: string) => {

      loading.dismiss()
      this.alert.show('Error', errorMsg)

    })

  }



  // Helpers

  hasOpenTimeslots() {
    if (this.prosAndTimeslots) {
      for (const proAndTimeslots of this.prosAndTimeslots) {
        if (proAndTimeslots.timeslots.length > 0) return true
      }
    }

    return false
  }

  formatServiceList(serviceList: EventServiceOffering[]) {
    return serviceList.map(service => service.serviceTitle).join(', ')
  }

  eventType() {
    if (!this.eventInfo) return ''

    if (Util.contains(this.eventInfo.serviceCategories, 'Hair')) {
      return 'Hair'
    }
    if (Util.contains(this.eventInfo.serviceCategories, 'Massage')) {
      return 'Massage'
    }
    if (Util.contains(this.eventInfo.serviceCategories, 'Nails')) {
      return 'Nails'
    }

    return 'Hair';
  }

  adjustedDate(d: Date, timezoneOffset: number) {
    return Util.getDateAdjustedForOffset(d, timezoneOffset)
  }

  prettyAdjustedDate(d: Date, timezoneOffset: number) {
    let adjustedDate = this.adjustedDate(d, timezoneOffset)
    return Util.dayMonthDateYear(adjustedDate)
  }

  roundedRating(rating: number) {
    return Math.round(rating)
  }

  toRange(n: number) {
    return Util.toRange(n)
  }

}
